import React from 'react';
import { Plus } from 'lucide-react';
import { FormDrawer } from './FormDrawer';
import {
  isDeliveredDeliveryStatus,
  isIncidentDeliveryStatus,
  isInTransitDeliveryStatus,
  normalizeDeliveryStatus,
} from '../../utils/deliveryStatus';

const formatDateShort = (iso) => {
  const raw = String(iso || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '—';
  const [y, m, d] = raw.split('-');
  return `${d}/${m}/${y}`;
};

/**
 * Formulario de entrega alineado al modelo:
 * Venta 1:N Entrega 1:N Tramo N:1 Viaje
 */
export const LogisticsDeliveryForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  isContinuation = false,
  formData,
  onChange,
  formError = '',
  salesOptions = [],
  tripOptions = [],
  providerOptions = [],
  linkedLegs = [],
  linkedProductRequests = [],
  onAddTrip,
}) => {
  const tripMode = formData?.trip_mode === 'iniciado' ? 'iniciado' : 'programado';
  const startingDelivery = Boolean(formData?.starting_delivery);
  const deliveryStatus = normalizeDeliveryStatus(formData?.delivery_status || 'Programada');
  const isCreateDelivery = !isEditing && !isContinuation && !startingDelivery;
  const isScheduledDelivery =
    !isContinuation &&
    !startingDelivery &&
    (isCreateDelivery || (isEditing && deliveryStatus === 'Programada'));
  const canStartDelivery =
    isEditing &&
    !isContinuation &&
    !isDeliveredDeliveryStatus(deliveryStatus) &&
    !isIncidentDeliveryStatus(deliveryStatus) &&
    !isInTransitDeliveryStatus(deliveryStatus);
  const deliveryKind = formData?.delivery_kind || 'Despacho Completo';
  const isReposicion =
    String(deliveryKind)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes('reposicion');
  const showTripSection = isContinuation || (!isEditing && tripMode === 'iniciado');

  const deliveryDateFields = [
    {
      key: 'created_by',
      label: 'Creado por',
      readOnly: true,
    },
    {
      key: 'promised_delivery_date',
      label: 'Fecha límite de Entrega',
      type: 'date',
      readOnly: true,
    },
    {
      key: 'scheduled_date',
      label: 'Fecha programada de envío',
      type: 'date',
    },
    {
      key: 'expected_delivery_date',
      label: 'Fecha programada de recepción',
      type: 'date',
    },
  ];

  const contextBanner = ({ colors }) => {
    const code = formData?.delivery_code || '—';
    const sale = [formData?.sale_code, formData?.organization_name].filter(Boolean).join(' · ') || '—';
    const numberOrDash = (value) => {
      const parsed = Number(value);
      if (!value && value !== 0) return '—';
      return Number.isFinite(parsed) ? parsed.toLocaleString('es-CL', { maximumFractionDigits: 2 }) : '—';
    };
    // La carga se define al iniciar la entrega: en la continuación sólo se muestra.
    const cargoFields = [
      { label: 'Guía de despacho', value: formData?.dispatch_guide || '—' },
      { label: 'Factura', value: formData?.invoice_number || '—' },
      { label: 'Peso (kg)', value: numberOrDash(formData?.weight_kg) },
      { label: 'Número de bultos', value: numberOrDash(formData?.package_count) },
    ];
    return (
      <div
        style={{
          padding: '10px 12px',
          borderRadius: '8px',
          border: `1px solid ${colors.sectionBorder}`,
          background: colors.sectionBg,
          fontSize: '12px',
          color: colors.text,
          lineHeight: 1.45,
        }}
      >
        <div>
          <strong>Entrega:</strong> {code}
        </div>
        <div style={{ color: colors.muted, marginTop: 2 }}>
          <strong>Venta:</strong> {sale}
        </div>
        {isContinuation ? (
          <>
            <div style={{ color: colors.muted, marginTop: 6 }}>
              Continuación de cadena: se crea un <strong>nuevo tramo</strong> de la misma entrega (no una entrega
              nueva). El transportista vive en el viaje y se propaga al tramo.
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '10px',
                marginTop: 10,
                paddingTop: 10,
                borderTop: `1px solid ${colors.sectionBorder}`,
              }}
            >
              {cargoFields.map((field) => (
                <div key={field.label}>
                  <div style={{ color: colors.muted, fontSize: '11px' }}>{field.label}</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>{field.value}</div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderTripSelect = ({ colors, baseInputStyle }) => (
    <div>
      <div style={{ fontSize: '12px', color: colors.text, marginBottom: 6 }}>Viaje</div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <select
          style={{ ...baseInputStyle, flex: 1, minWidth: 0, marginTop: 0 }}
          value={formData?.trip_id || ''}
          onChange={(event) => onChange('trip_id', event.target.value)}
        >
          <option value="">Seleccionar viaje</option>
          {tripOptions.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {typeof onAddTrip === 'function' ? (
          <button
            type="button"
            onClick={onAddTrip}
            title="Nuevo viaje"
            aria-label="Nuevo viaje"
            style={{
              flexShrink: 0,
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              border: `1px solid ${colors.inputBorder}`,
              background: colors.inputBg,
              color: colors.text,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <Plus size={16} />
          </button>
        ) : null}
      </div>
      <div style={{ fontSize: '11px', color: colors.muted, marginTop: 6 }}>
        Si el viaje no existe, créalo con <strong>+</strong> (igual que organizaciones en cotizaciones).
      </div>
    </div>
  );

  const sharedNotesSection = {
    key: 'shared_notes',
    title: 'Notas y comentarios compartidos',
    fields: [
      {
        key: '_shared_notes_hint',
        label: 'Info',
        type: 'custom',
        fullWidth: true,
        render: ({ colors }) => (
          <div style={{ fontSize: '11px', color: colors.muted, lineHeight: 1.4 }}>
            Las mismas notas de la cotización / venta. Al guardar se actualizan en la cotización asociada.
          </div>
        ),
      },
      {
        key: 'quotation_comments',
        label: 'Comentarios',
        type: 'textarea',
        rows: 4,
        fullWidth: true,
        placeholder: 'Un comentario por línea',
      },
      {
        key: 'quotation_observations',
        label: 'Observaciones internas',
        type: 'textarea',
        rows: 3,
        fullWidth: true,
        placeholder: 'No se muestran en el PDF al cliente',
      },
    ],
  };

  const scheduledInfoFields = [
    {
      key: 'delivery_code',
      label: 'Código Entrega',
      readOnly: true,
      placeholder: isCreateDelivery ? 'Se genera al guardar' : undefined,
    },
    isCreateDelivery
      ? {
          key: 'sale_id',
          label: 'Código Venta',
          type: 'select',
          options: [{ value: '', label: 'Seleccionar venta' }, ...salesOptions],
        }
      : {
          key: 'sale_code',
          label: 'Código Venta',
          readOnly: true,
        },
    {
      key: 'created_by',
      label: 'Creado por',
      readOnly: true,
    },
    {
      key: 'promised_delivery_date',
      label: 'Fecha límite de Entrega',
      type: 'date',
      readOnly: !isCreateDelivery,
    },
    {
      key: 'scheduled_date',
      label: 'Fecha programada de envío',
      type: 'date',
    },
    {
      key: 'expected_delivery_date',
      label: 'Fecha programada de recepción',
      type: 'date',
    },
    {
      key: 'pickup_warehouse',
      label: 'Origen',
      readOnly: !isCreateDelivery,
    },
    {
      key: 'delivery_address',
      label: 'Dirección de Entrega',
    },
    {
      key: 'receiver_name',
      label: 'Contacto Recepción',
    },
    {
      key: 'receiver_phone',
      label: 'Fono Recepción',
    },
  ];

  const asReadOnlyFields = (fields) =>
    fields.map((field) => ({
      ...field,
      readOnly: true,
      disabled: true,
    }));

  const renderStartItemsTable = ({ colors, baseInputStyle }) => {
    const items = Array.isArray(formData?.start_items) ? formData.start_items : [];
    const shipAll = Boolean(formData?.ship_all);
    const locked = shipAll;
    const updateItems = (next) => onChange('start_items', next);
    const headerCell = {
      textAlign: 'left',
      padding: '6px 8px',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.muted,
      borderBottom: `1px solid ${colors.sectionBorder}`,
      whiteSpace: 'nowrap',
    };
    const bodyCell = {
      padding: '6px 8px',
      fontSize: '12px',
      color: colors.text,
      borderBottom: `1px solid ${colors.sectionBorder}`,
      verticalAlign: 'middle',
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: colors.text }}>Ítems enviados</div>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            color: colors.text,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={shipAll}
            onChange={(event) => onChange('ship_all', event.target.checked)}
            style={{ marginTop: '2px' }}
          />
          <span>
            <strong>Se envió todo el pedido</strong>
            <div style={{ color: colors.muted, marginTop: 2 }}>
              Marca todos los ítems cotizados con su cantidad completa y deja la tabla no editable.
            </div>
          </span>
        </label>
        {items.length ? (
          <div
            style={{
              overflowX: 'auto',
              border: `1px solid ${colors.sectionBorder}`,
              borderRadius: '8px',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCell, width: 36 }}>Envía</th>
                  <th style={headerCell}>Ítem</th>
                  <th style={headerCell}>SKU</th>
                  <th style={{ ...headerCell, textAlign: 'right' }}>Cotizado</th>
                  <th style={{ ...headerCell, textAlign: 'right', width: 110 }}>Cant. enviada</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const quoted = Number(item.quoted_qty || 0);
                  const selected = shipAll ? true : Boolean(item.selected);
                  const quantity = shipAll ? quoted : Number(item.quantity || 0);
                  return (
                    <tr
                      key={String(item.quotation_item_id || item.product_sku || index)}
                      style={{ opacity: selected ? 1 : 0.55 }}
                    >
                      <td style={bodyCell}>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={locked}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            updateItems(
                              items.map((row, i) =>
                                i === index
                                  ? {
                                      ...row,
                                      selected: checked,
                                      quantity: checked ? Number(row.quantity || row.quoted_qty || 0) : 0,
                                    }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td style={bodyCell}>
                        <div style={{ fontWeight: 600 }}>{item.item_name || '—'}</div>
                        {item.color ? (
                          <div style={{ fontSize: '11px', color: colors.muted }}>{item.color}</div>
                        ) : null}
                      </td>
                      <td style={{ ...bodyCell, color: colors.muted }}>{item.product_sku || '—'}</td>
                      <td style={{ ...bodyCell, textAlign: 'right' }}>
                        {quoted}
                        {item.unit ? ` ${item.unit}` : ''}
                      </td>
                      <td style={bodyCell}>
                        <input
                          type="number"
                          min="0"
                          max={quoted || undefined}
                          step="0.01"
                          disabled={locked || !selected}
                          value={selected ? quantity : 0}
                          onChange={(event) => {
                            const nextQty = Math.max(0, Number(event.target.value || 0));
                            updateItems(
                              items.map((row, i) =>
                                i === index
                                  ? {
                                      ...row,
                                      quantity: nextQty,
                                      selected: nextQty > 0,
                                    }
                                  : row
                              )
                            );
                          }}
                          style={{
                            ...baseInputStyle,
                            marginTop: 0,
                            height: '30px',
                            textAlign: 'right',
                            opacity: locked || !selected ? 0.7 : 1,
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: colors.muted }}>
            No hay ítems cotizados asociados a esta venta.
          </div>
        )}
      </div>
    );
  };

  const renderLinkedProductRequests = ({ colors }) => {
    const rows = Array.isArray(linkedProductRequests) ? linkedProductRequests : [];
    if (!rows.length) {
      return (
        <div style={{ fontSize: '12px', color: colors.muted, lineHeight: 1.45 }}>
          No hay solicitudes de producto asociadas a esta entrega.
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {rows.map((req) => {
          const code = req.request_code || req.code || `REQ-${req.id}`;
          const status = req.status || '—';
          const needed = String(req.needed_by_date || '').slice(0, 10);
          const itemsCount = req.items_count != null ? `${req.items_count} productos` : '';
          return (
            <div
              key={req.id || code}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: `1px solid ${colors.sectionBorder || colors.border}`,
                background: colors.sectionBg || colors.inputBg,
                fontSize: '12px',
                color: colors.text,
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 700 }}>{code}</div>
              <div style={{ color: colors.muted, marginTop: 2 }}>
                {[status, needed ? `Límite ${formatDateShort(needed)}` : '', itemsCount]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {req.notes ? (
                <div style={{ color: colors.muted, marginTop: 4, whiteSpace: 'pre-wrap' }}>{req.notes}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const productRequestsSection = {
    key: 'product_requests',
    title: 'Solicitudes de producto asociadas',
    defaultOpen: true,
    fields: [
      {
        key: '_linked_product_requests',
        label: 'Solicitudes',
        type: 'custom',
        fullWidth: true,
        render: ({ colors }) => renderLinkedProductRequests({ colors }),
      },
    ],
  };

  const scheduledSections = [
    {
      key: 'scheduled',
      title: 'Entrega programada',
      fields: scheduledInfoFields,
    },
    ...(isEditing || formData?.id ? [productRequestsSection] : []),
    sharedNotesSection,
  ];

  const startSections = [
    {
      key: 'start_context',
      title: 'Entrega programada',
      defaultOpen: false,
      fields: [
        ...asReadOnlyFields(scheduledInfoFields),
        ...asReadOnlyFields(
          sharedNotesSection.fields.filter((field) => field.key !== '_shared_notes_hint')
        ),
      ],
    },
    {
      key: 'start_cargo',
      title: 'Información de la Carga',
      defaultOpen: true,
      fields: [
        {
          key: 'package_count',
          label: 'Cantidad de bultos',
          type: 'number',
          step: '1',
        },
        {
          key: 'weight_kg',
          label: 'Peso (kg)',
          type: 'number',
          step: '0.01',
        },
        {
          key: 'dispatch_guide',
          label: 'Guía de Despacho',
          placeholder: 'Número de guía',
        },
        {
          key: 'invoice_number',
          label: 'Factura',
          placeholder: 'N° de factura',
        },
      ],
    },
    {
      key: 'start_leg',
      title: 'Tramo Inicial',
      defaultOpen: true,
      columns: 3,
      fields: [
        {
          key: '_trip_select',
          label: 'Viaje',
          type: 'custom',
          fullWidth: true,
          render: ({ colors, baseInputStyle }) => renderTripSelect({ colors, baseInputStyle }),
        },
        {
          key: 'started_at',
          label: 'Fecha y Hora de inicio',
          type: 'datetime-local',
          readOnly: true,
          disabled: true,
        },
        {
          key: 'provider_code',
          label: 'Proveedor Logístico',
          type: 'select',
          readOnly: true,
          disabled: true,
          options: [{ value: '', label: 'Seleccionar proveedor' }, ...providerOptions],
        },
        {
          key: 'carrier_name',
          label: 'Nombre Transportista',
          readOnly: true,
          disabled: true,
        },
        {
          key: '_origin_dest',
          label: 'Origen y destino',
          type: 'custom',
          fullWidth: true,
          render: ({ colors, baseInputStyle }) => (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              <label style={{ fontSize: '12px', color: colors.text }}>
                Origen
                <input
                  type="text"
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={formData?.pickup_warehouse || ''}
                  onChange={(event) => onChange('pickup_warehouse', event.target.value)}
                  placeholder="Origen del tramo"
                />
              </label>
              <label style={{ fontSize: '12px', color: colors.text }}>
                Destino
                <input
                  type="text"
                  style={{ ...baseInputStyle, marginTop: '6px' }}
                  value={formData?.delivery_address || ''}
                  onChange={(event) => onChange('delivery_address', event.target.value)}
                  placeholder="Destino del tramo"
                />
              </label>
            </div>
          ),
        },
      ],
    },
    {
      key: 'start_items',
      title: 'Listado de Items',
      defaultOpen: true,
      fields: [
        {
          key: '_start_items',
          label: 'Ítems',
          type: 'custom',
          fullWidth: true,
          render: ({ colors, baseInputStyle }) => renderStartItemsTable({ colors, baseInputStyle }),
        },
      ],
    },
  ];

  const sections = isScheduledDelivery
    ? scheduledSections
    : startingDelivery
      ? startSections
      : [
    ...(isContinuation
      ? [
          {
            key: 'context',
            title: 'Contexto',
            fields: [
              {
                key: '_context',
                label: 'Entrega',
                type: 'custom',
                fullWidth: true,
                render: ({ colors }) => contextBanner({ colors }),
              },
            ],
          },
        ]
      : [
          {
            key: 'sale',
            title: 'Venta',
            fields: [
              {
                key: 'sale_id',
                label: 'Venta asociada',
                type: 'select',
                options: [{ value: '', label: 'Seleccionar venta' }, ...salesOptions],
                disabled: isEditing,
                fullWidth: true,
              },
              ...(formData?.sale_code || formData?.organization_name
                ? [
                    {
                      key: '_sale_meta',
                      label: 'Detalle',
                      type: 'custom',
                      fullWidth: true,
                      render: ({ colors }) => (
                        <div style={{ fontSize: '12px', color: colors.muted, lineHeight: 1.4 }}>
                          {[formData.sale_code, formData.quotation_code, formData.organization_name]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]),

    ...(!isContinuation
      ? [
          {
            key: 'delivery',
            title: 'Entrega',
            fields: [
              {
                key: 'delivery_code',
                label: 'Código entrega',
                placeholder: 'Auto: DLV0344-A / DLV0344-AR',
                readOnly: isEditing && Boolean(formData?.delivery_code),
              },
              {
                key: 'delivery_kind',
                label: 'Tipo',
                type: 'select',
                options: [
                  { value: 'Despacho Completo', label: 'Despacho Completo' },
                  { value: 'Despacho Parcial', label: 'Despacho Parcial' },
                  { value: 'Reposición', label: 'Reposición (ítems ya enviados)' },
                  { value: 'Retiro en Bodega', label: 'Retiro en Bodega' },
                ],
              },
              {
                key: 'delivery_status',
                label: 'Estado',
                type: 'custom',
                fullWidth: true,
                render: ({ colors }) => {
                  const isIncident = isIncidentDeliveryStatus(deliveryStatus);
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '12px', color: colors.text }}>
                        Estado: <strong>{deliveryStatus}</strong>
                      </div>
                      <div style={{ fontSize: '11px', color: colors.muted, lineHeight: 1.4 }}>
                        Se actualiza solo según fecha prometida y tramos (a tiempo / con retraso / conforme).
                      </div>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          color: colors.text,
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isIncident}
                          onChange={(event) => {
                            if (event.target.checked) {
                              onChange('_status_before_incident', deliveryStatus);
                              onChange('delivery_status', 'Entrega Con Incidente');
                            } else {
                              onChange(
                                'delivery_status',
                                formData?._status_before_incident || 'Programada'
                              );
                            }
                          }}
                          style={{ marginTop: '2px' }}
                        />
                        <span>Marcar entrega con incidente (manual)</span>
                      </label>
                    </div>
                  );
                },
              },
              ...deliveryDateFields,
              { key: 'delivered_date', label: 'Fecha entrega real', type: 'date' },
              { key: 'weight_kg', label: 'Peso enviado (kg)', type: 'number' },
              {
                key: 'dispatch_guide',
                label: 'Guía de despacho',
                placeholder: 'Común a todos los tramos de esta entrega',
              },
              { key: 'pickup_warehouse', label: 'Origen', readOnly: true },
              {
                key: 'delivery_address',
                label: 'Dirección de entrega',
              },
              { key: 'receiver_name', label: 'Recibe' },
              {
                key: 'notes',
                label: 'Notas operativas de esta entrega',
                type: 'textarea',
                rows: 2,
                fullWidth: true,
                placeholder: 'Solo este registro (no se comparten)',
              },
              ...(isReposicion && !isEditing
                ? [
                    {
                      key: 'confirm_reposicion',
                      label: 'Confirmar reposición',
                      type: 'custom',
                      fullWidth: true,
                      render: ({ value, onChange: onFieldChange, colors }) => {
                        const checked = value === true || value === 'true' || value === '1';
                        return (
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              color: colors.text,
                              fontSize: '12px',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: `1px solid ${checked ? '#f59e0b' : colors.sectionBorder}`,
                              background: checked ? (isDarkMode ? '#422006' : '#fffbeb') : colors.sectionBg,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => onFieldChange(e.target.checked)}
                              style={{ marginTop: '2px' }}
                            />
                            <span>
                              <strong>Confirmo que es una reposición</strong>
                              <div style={{ color: colors.muted, marginTop: 4 }}>
                                La venta puede no tener saldo pendiente o los ítems ya fueron enviados. No es un
                                despacho duplicado por error.
                              </div>
                            </span>
                          </label>
                        );
                      },
                    },
                  ]
                : []),
            ],
          },
          sharedNotesSection,
        ]
      : []),

    ...(isEditing && !isContinuation
      ? [
          {
            key: 'legs_summary',
            title: 'Tramos de esta entrega',
            fields: [
              {
                key: '_legs',
                label: 'Tramos',
                type: 'custom',
                fullWidth: true,
                render: ({ colors }) => {
                  if (!linkedLegs.length) {
                    return (
                      <div style={{ fontSize: '12px', color: colors.muted }}>
                        Sin tramos registrados. Al iniciar la entrega se crea el tramo 01 con el viaje elegido.
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {linkedLegs.map((leg) => (
                        <div
                          key={leg.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '4px 12px',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.sectionBorder}`,
                            background: colors.sectionBg,
                            fontSize: '12px',
                            color: colors.text,
                          }}
                        >
                          <div>
                            <strong>{leg.leg_code || `Tramo #${leg.id}`}</strong>
                            <div style={{ color: colors.muted }}>{leg.leg_status || '—'}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div>{leg.trip_code || 'Sin viaje'}</div>
                            <div style={{ color: colors.muted }}>
                              ETA {formatDateShort(leg.eta_close)} · {leg.carrier_name || '—'}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div style={{ fontSize: '11px', color: colors.muted, marginTop: 2 }}>
                        Para agregar otro tramo usa <strong>Continuar</strong> en la tabla. El viaje se asigna al
                        tramo.
                      </div>
                    </div>
                  );
                },
              },
            ],
          },
        ]
      : []),

    ...(canStartDelivery && !startingDelivery
      ? [
          {
            key: 'start_action',
            title: 'Inicio operativo',
            fields: [
              {
                key: '_start_delivery',
                label: 'Iniciar',
                type: 'custom',
                fullWidth: true,
                render: ({ colors }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '12px', color: colors.muted, lineHeight: 1.45 }}>
                      Al iniciar se crea o actualiza el tramo inicial y se le asigna un viaje. El estado pasa a En
                      Ruta (a tiempo o con retraso según la fecha prometida).
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onChange('starting_delivery', true);
                        onChange('trip_mode', 'iniciado');
                      }}
                      style={{
                        alignSelf: 'flex-start',
                        height: '36px',
                        borderRadius: '8px',
                        padding: '0 14px',
                        border: 'none',
                        backgroundColor: '#2563eb',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      Iniciar entrega
                    </button>
                  </div>
                ),
              },
            ],
          },
        ]
      : []),

    ...(!isEditing && !isContinuation
      ? [
          {
            key: 'start_mode',
            title: 'Inicio operativo',
            fields: [
              {
                key: 'trip_mode',
                label: 'Modo',
                type: 'custom',
                fullWidth: true,
                render: ({ colors }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        color: colors.text,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="trip_mode"
                        checked={tripMode === 'programado'}
                        onChange={() => onChange('trip_mode', 'programado')}
                        style={{ marginTop: '2px' }}
                      />
                      <span>
                        <strong>Solo programar entrega</strong>
                        <div style={{ color: colors.muted, marginTop: 2 }}>
                          Queda en calendario. Se crea el tramo 01 sin viaje; el viaje se asigna al iniciar.
                        </div>
                      </span>
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        color: colors.text,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="trip_mode"
                        checked={tripMode === 'iniciado'}
                        onChange={() => onChange('trip_mode', 'iniciado')}
                        style={{ marginTop: '2px' }}
                      />
                      <span>
                        <strong>Iniciar primer tramo</strong>
                        <div style={{ color: colors.muted, marginTop: 2 }}>
                          Asocia o crea un viaje, define ETA y tracking. El transportista del viaje se propaga al
                          tramo.
                        </div>
                      </span>
                    </label>
                  </div>
                ),
              },
            ],
          },
        ]
      : []),

    ...(showTripSection
      ? [
          {
            key: 'leg_trip',
            title: isContinuation
              ? 'Nuevo tramo → Viaje'
              : startingDelivery
                ? 'Iniciar entrega → Viaje'
                : 'Primer tramo → Viaje',
            fields: [
              {
                key: '_trip_select',
                label: 'Viaje',
                type: 'custom',
                fullWidth: true,
                render: ({ colors, baseInputStyle }) => renderTripSelect({ colors, baseInputStyle }),
              },
              {
                key: 'eta_close',
                label: 'ETA cierre del tramo',
                type: 'date',
              },
              {
                key: 'tracking_code',
                label: 'Tracking del tramo',
                placeholder: 'Código courier de este tramo',
              },
              ...(isContinuation
                ? [
                    {
                      key: 'pickup_warehouse',
                      label: 'Origen del tramo',
                      placeholder: 'Destino del viaje anterior',
                    },
                    {
                      key: 'delivery_address',
                      label: 'Destino del tramo',
                      placeholder: 'Cliente o siguiente hub',
                    },
                    {
                      key: 'notes',
                      label: 'Notas del tramo',
                      type: 'textarea',
                      rows: 3,
                      fullWidth: true,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const title = isCreateDelivery
    ? 'Nueva entrega'
    : isScheduledDelivery
      ? 'Entrega programada'
      : isEditing
        ? startingDelivery
          ? 'Iniciar entrega'
          : 'Editar entrega'
        : isContinuation
          ? 'Continuar tramo'
          : 'Nueva entrega';

  const subtitle = isScheduledDelivery
    ? isCreateDelivery
      ? 'Misma planificación que una entrega programada. El código se genera al guardar.'
      : 'Planificación y notas compartidas de cotización / venta'
    : isContinuation
      ? 'Misma entrega · nuevo tramo · viaje/transportista propios · tracking independiente'
      : startingDelivery
        ? 'Información de carga, tramo inicial con viaje e ítems a enviar'
        : 'Venta → Entrega → Tramos. El viaje se vincula al iniciar o en cada tramo.';

  return (
    <FormDrawer
      key={startingDelivery ? 'start' : isScheduledDelivery ? 'scheduled' : 'full'}
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={title}
      subtitle={subtitle}
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={startingDelivery ? 760 : 640}
      saveLabel={startingDelivery ? 'Confirmar inicio' : 'Guardar'}
    />
  );
};

export default LogisticsDeliveryForm;
